import { round } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AudioPlayer from "react-h5-audio-player";
import { FacebookShareButton, TwitterShareButton } from "react-share";
import CopyToClipboard from "react-copy-to-clipboard";

import { Recorder } from "./Recorder";

import "react-h5-audio-player/src/styles.scss";
import "./RecordingModal.scss";

/*
import headingFinished from "../assets/images/modalHeading--finished.svg";
import headingPreview from "../assets/images/modalHeading--preview.svg";
import headingNameYourLabel from "../assets/images/modalHeading--nameYourLabel.svg";
import headingReleaseYourMasterpiece from "../assets/images/modalHeading--releaseYourMasterpiece.svg";
*/

import buttonClose from "../assets/images/modalButton--close.svg";
import buttonShowMeMyPiece from "../assets/images/modalButton--showMeMyPiece.svg";
import buttonStartAgain from "../assets/images/modalButton--startAgain.svg";
import buttonNext from "../assets/images/modalButton--next.svg";
import buttonNameIt from "../assets/images/modalButton--nameIt.svg";

import buttonShareFacebook from "../assets/images/iconShare--facebook.svg";
import buttonShareTwitter from "../assets/images/iconShare--twitter.svg";
import buttonShareDownload from "../assets/images/iconShare--download.svg";
import buttonShareLink from "../assets/images/iconShare--link.svg";

import labelAuthor from "../assets/images/modalLabel--author.svg";
import labelNameYourRecord from "../assets/images/modalLabel--nameYourRecord.svg";

interface RecordingModalProps {
  recorder: Recorder;
  onClose: (startNewRecording: boolean) => void;
}
export const RecordingModal: React.FC<RecordingModalProps> = React.memo(
  ({ recorder, onClose }) => {
    let [step, setStep] = useState<
      | "recordingFinished"
      | "playMyTrack"
      | "labelMyTrack"
      | "encodingTrack"
      | "releaseMyTrack"
    >("recordingFinished");

    return (
      <div
        className="recordingModalBackground"
        onClick={(evt) => evt.target === evt.currentTarget && onClose(false)}
      >
        <div className="recordingModal">
          <button
            className="modalButton modalButton--close"
            onClick={() => onClose(false)}
          >
            <img src={buttonClose} alt="Close" />
          </button>
          {step === "recordingFinished" && (
            <RecordingFinishedStep
              onPlayMyTrack={() => setStep("playMyTrack")}
              onStartAgain={() => onClose(true)}
            />
          )}
          {step === "playMyTrack" && (
            <PlayMyTrackStep
              track={recorder.recording!}
              onShareMyTrack={() => {
                setStep("labelMyTrack");
                recorder.prepareEncoder();
              }}
            />
          )}
          {step === "labelMyTrack" && (
            <LabelMyTrackStep
              onShare={(artistName, trackName) => {
                setStep("encodingTrack");
                recorder.startEncoder(artistName, trackName);
              }}
            />
          )}
          {step === "encodingTrack" && (
            <EncodingTrackStep
              recorder={recorder}
              onEncoded={() => setStep("releaseMyTrack")}
            />
          )}
          {step === "releaseMyTrack" && (
            <ReleaseMyTrackStep recorder={recorder} />
          )}
        </div>
      </div>
    );
  }
);

interface RecordingFinishedStepProps {
  onPlayMyTrack: () => void;
  onStartAgain: () => void;
}
const RecordingFinishedStep: React.FC<RecordingFinishedStepProps> = ({
  onPlayMyTrack,
  onStartAgain,
}) => (
  <div className="recordingModalContent recordingModalContent--options">
    <div className="recordingModalHeader">
      <h2>Finished?</h2>
    </div>
    <div className="recordingModalBody">
      <button
        className="modalButton modalButton--showMeMyPiece"
        onClick={onPlayMyTrack}
      >
        <img src={buttonShowMeMyPiece} alt="Show me my piece" />
      </button>
      <button
        className="modalButton modalButton--startAgain"
        onClick={onStartAgain}
      >
        <img src={buttonStartAgain} alt="Start again" />
      </button>
    </div>
  </div>
);

interface PlayMyTrackStepProps {
  track: Blob;
  onShareMyTrack: () => void;
}
const PlayMyTrackStep: React.FC<PlayMyTrackStepProps> = ({
  track,
  onShareMyTrack,
}) => {
  let [trackUrl, setTrackUrl] = useState<string>();

  useEffect(() => {
    let u = URL.createObjectURL(track);
    setTrackUrl(u);
    return () => {
      URL.revokeObjectURL(u);
    };
  }, [track]);

  return (
    <div className="recordingModalContent">
      <div className="recordingModalHeader">
        <h2>Preview</h2>
      </div>
      <div className="recordingModalBody">
        <AudioPlayer
          src={trackUrl}
          layout="horizontal-reverse"
          showJumpControls={false}
          customVolumeControls={[]}
          customAdditionalControls={[]}
        />
        <button
          className="modalButton modalButton--next"
          onClick={onShareMyTrack}
        >
          <img src={buttonNext} alt="Next" />
        </button>
      </div>
    </div>
  );
};

interface LabelMyTrackStepProps {
  onShare: (artistName: string, trackName: string) => void;
}
const LabelMyTrackStep: React.FC<LabelMyTrackStepProps> = ({ onShare }) => {
  let [artistName, setArtistName] = useState("");
  let [trackName, setTrackName] = useState("");

  return (
    <div className="recordingModalContent recordingModalContent--label">
      <div className="recordingModalHeader">
        <h2>
          Name
          <br /> your tune
        </h2>
      </div>
      <div className="recordingModalBody">
        <label className="modalInput modalInput--author">
          <span className="modalInputLabel modalInputLabel--author">
            <img src={labelAuthor} alt="Author" />
          </span>
          <input
            type="text"
            value={artistName}
            onChange={(evt) => setArtistName(evt.target.value)}
          />
        </label>
        <label className="modalInput modalInput--nameYourRecord">
          <span className="modalInputLabel">
            <img src={labelNameYourRecord} alt="Name your record" />
          </span>
          <input
            type="text"
            value={trackName}
            onChange={(evt) => setTrackName(evt.target.value)}
          />
        </label>
        <button
          className="modalButton modalButton--nameIt"
          onClick={() => onShare(artistName, trackName)}
        >
          <img src={buttonNameIt} alt="Name it" />
        </button>
      </div>
    </div>
  );
};

interface EncodingTrackStepProps {
  recorder: Recorder;
  onEncoded: () => void;
}
const EncodingTrackStep: React.FC<EncodingTrackStepProps> = ({
  recorder,
  onEncoded,
}) => {
  let [progress, setProgress] = useState(0);

  const circumference = 132.5 * 2 * Math.PI;
  const percentComplete = progress * 100;
  const strokeDashOffset =
    circumference - (percentComplete / 100) * circumference;

  useEffect(() => {
    let onProgress = setProgress;
    recorder.on("encoderProgress", onProgress);
    recorder.on("encoderDone", onEncoded);
    return () => {
      recorder.off("encoderProgress", onProgress);
      recorder.off("encoderDone", onEncoded);
    };
  }, [recorder, onEncoded]);

  return (
    <div className="recordingModalContent recordingModalContent--processing">
      <svg
        className="progressIndictor"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 273 273"
      >
        <circle className="background" cx="136.5" cy="136.5" r="105" />
        <circle className="outline" cx="136.5" cy="136.5" r="132.5" />
        <circle
          className="progress"
          cx="136.5"
          cy="136.5"
          r="132.5"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashOffset}
          stroke-linecap="round"
        />
      </svg>
      <div className="progressText">{round(progress * 100)}%</div>
    </div>
  );
};

interface ReleaseMyTrackStepProps {
  recorder: Recorder;
}
const ReleaseMyTrackStep: React.FC<ReleaseMyTrackStepProps> = ({
  recorder,
}) => {
  let webShareSupported = useMemo(() => "share" in navigator, []);

  let videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!videoRef.current) return;
    let posterUrl = URL.createObjectURL(recorder.finalPoster);
    let videoUrl = URL.createObjectURL(recorder.finalVideo);
    videoRef.current.poster = posterUrl;
    videoRef.current.src = videoUrl;
    videoRef.current.play().catch(() => console.warn("Autoplay not allowed"));
    return () => {
      URL.revokeObjectURL(posterUrl);
      URL.revokeObjectURL(videoUrl);
    };
  }, [recorder]);

  let webShare = useCallback(() => {
    let shareFile = new File(
      [recorder.finalVideo!],
      recorder.getFilename("mp4"),
      { lastModified: Date.now(), type: recorder.finalVideo!.type }
    );
    let canShareFile =
      "canShare" in navigator &&
      (navigator as any).canShare({ files: [shareFile] });

    let trackMeta = getTrackDescription(recorder);

    if (canShareFile) {
      navigator.share({
        files: [shareFile],
        text: trackMeta,
        url: recorder.shareLink!,
      } as any);
    } else {
      navigator.share({
        title: `Roland 808303.studio`,
        text: trackMeta,
        url: recorder.shareLink!,
      });
    }
  }, [recorder]);

  let downloadTrack = useCallback(() => {
    recorder.download();
  }, [recorder]);

  return (
    <div className="recordingModalContent recordingModalContent--share">
      <div className="recordingModalHeader">
        <h2>
          Release your
          <br /> masterpiece
        </h2>
      </div>
      <div className="recordingModalBody">
        <video className="videoPlayer" ref={videoRef} controls />
        <div className="shareButtons">
          {webShareSupported && (
            <button
              className="shareButton shareButton--general"
              onClick={webShare}
            >
              <img src={buttonShareLink} alt="Share link" />
            </button>
          )}
          {!webShareSupported && (
            <>
              <FacebookShareButton
                url={recorder.shareLink!}
                title="Made a track using Roland 808303.studio @yurisuzuki @DesignMuseum @Roland_UK"
                className="shareButton shareButton--facebook"
              >
                <img src={buttonShareFacebook} alt="Share on Facebook" />
              </FacebookShareButton>{" "}
              <TwitterShareButton
                url={recorder.shareLink!}
                title="Made a track using Roland 808303.studio #808303studio @yurisuzuki @DesignMuseum @Roland_UK"
                className="shareButton shareButton--twitter"
              >
                <img src={buttonShareTwitter} alt="Share on Twitter" />
              </TwitterShareButton>
            </>
          )}

          <button
            className="shareButton shareButton--download"
            onClick={downloadTrack}
          >
            <img src={buttonShareDownload} alt="Download" />
          </button>
          {!webShareSupported && (
            <CopyToClipboard text={recorder.shareLink!}>
              <button className="shareButton shareButton--link">
                <img src={buttonShareLink} alt="Share link" />
              </button>
            </CopyToClipboard>
          )}
        </div>
      </div>
    </div>
  );
};
function getTrackDescription(recorder: Recorder) {
  let artistName = recorder.artistName || "";
  let trackName = recorder.trackName || "";
  let trackMeta = "";
  if (artistName.length > 0 && trackName.length > 0) {
    trackMeta = `"${trackName}" by ${artistName} made using the music creation platform from @yurisuzuki @DesignMuseum @Roland_UK.`;
  } else if (trackName.length > 0) {
    trackMeta = `"${trackName}" made using the music creation platform from @yurisuzuki @DesignMuseum @Roland_UK.`;
  } else if (artistName.length > 0) {
    trackMeta = `A track by ${artistName} made using the music creation platform from @yurisuzuki @DesignMuseum @Roland_UK.`;
  } else {
    trackMeta = `A track made using the music creation platform from @yurisuzuki @DesignMuseum @Roland_UK.`;
  }
  return trackMeta;
}
